import { useDeferredValue, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { SearchBox, tokens } from "@fluentui/react-components";
import { helpText } from "./strings";
import {
  groupStepsBySection,
  localizedSection,
  localizedStep,
  stepMatchesQuery,
  type HelpLanguage,
  type HelpStep,
  type PageHelp,
} from "./types";
import { HELP_PANEL_ATTR } from "./dom";
import { Z } from "./ui";

interface Props {
  page: PageHelp;
  steps: HelpStep[];
  current: number;
  language: HelpLanguage;
  anchor: DOMRect;
  /** The control that toggles the list — a press on it must not count as "outside". */
  toggleRef?: RefObject<HTMLElement | null>;
  onJump: (index: number) => void;
  onClose: () => void;
}

/** The tour's step counter opens this: a grouped, searchable index of the tour's steps. */
export function HelpStepJump({ page, steps, current, language, anchor, toggleRef, onJump, onClose }: Props) {
  const t = helpText(language);
  const [query, setQuery] = useState("");
  const deferred = useDeferredValue(query);
  const ref = useRef<HTMLDivElement>(null);

  const groups = useMemo(() => {
    const matching = steps.filter((s) => stepMatchesQuery(s, deferred));
    return groupStepsBySection(matching, page.sections);
  }, [steps, deferred, page.sections]);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (toggleRef?.current?.contains(target)) return;
      if (ref.current && !ref.current.contains(target)) onClose();
    };
    window.addEventListener("pointerdown", onDown, true);
    return () => window.removeEventListener("pointerdown", onDown, true);
  }, [onClose, toggleRef]);

  const width = 340;
  const left = Math.min(Math.max(anchor.left, 12), window.innerWidth - width - 12);
  const maxHeight = Math.min(420, window.innerHeight - 24);
  const top =
    anchor.bottom + 6 + maxHeight < window.innerHeight
      ? anchor.bottom + 6
      : Math.max(12, anchor.top - 6 - maxHeight);

  return (
    <div
      ref={ref}
      {...{ [HELP_PANEL_ATTR]: "jump" }}
      data-help="help-tour-jump-list"
      role="listbox"
      style={{
        position: "fixed",
        top,
        left,
        width,
        maxHeight,
        zIndex: Z.stepJump,
        display: "flex",
        flexDirection: "column",
        background: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: 8,
        boxShadow: tokens.shadow16,
      }}
    >
      <div style={{ padding: 8 }}>
        <SearchBox
          autoFocus
          size="small"
          style={{ width: "100%", maxWidth: "100%" }}
          placeholder={t("help.jump.search", "Search this page's guide…")}
          value={query}
          onChange={(_, d) => setQuery(d.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              // the LIVE query, not the deferred list — otherwise Enter lands on the previous keystroke's result
              const hit = steps.findIndex((s) => stepMatchesQuery(s, query));
              if (hit >= 0) onJump(hit);
            }
          }}
        />
      </div>
      <div style={{ overflowY: "auto", padding: "0 0 8px" }}>
        {groups.length === 0 && (
          <div style={{ padding: "8px 12px", color: tokens.colorNeutralForeground3 }}>
            {t("help.search.noResults", "No step matches.")}
          </div>
        )}
        {groups.map((group) => (
          <div key={group.section}>
            <div
              style={{
                padding: "8px 12px 4px",
                fontSize: 11,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                color: tokens.colorNeutralForeground3,
              }}
            >
              {localizedSection(page, group.section, language).value}
            </div>
            {group.steps.map((step) => {
              const index = steps.indexOf(step);
              const active = index === current;
              return (
                <button
                  key={step.selector}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => onJump(index)}
                  style={{
                    display: "flex",
                    gap: 8,
                    width: "100%",
                    padding: "6px 12px",
                    border: 0,
                    textAlign: "left",
                    cursor: "pointer",
                    font: "inherit",
                    fontSize: 13,
                    color: "inherit",
                    background: active ? tokens.colorBrandBackground2 : "transparent",
                  }}
                >
                  <span style={{ minWidth: 22, color: tokens.colorNeutralForeground3 }}>
                    {index + 1}
                  </span>
                  <span>{localizedStep(step, language).value.title}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
